import React, { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, SlidersHorizontal, X } from 'lucide-react';
import { ProductCard } from '../components/ProductCard';
import { products } from '../data/products';
import { getCategoryBySlug, categories } from '../data/categories';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';

const CategoryPage = () => {
  const { slug } = useParams();
  const [sortBy, setSortBy] = useState('featured');
  const [filters, setFilters] = useState({
    petType: [],
    priceRange: [],
    rating: null
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const category = getCategoryBySlug(slug);
  
  // Get products for this category or all products if category not found
  const categoryProducts = useMemo(() => {
    if (!category) {
      return products;
    }
    return products.filter(p => p.category === slug || p.petType === category.petType);
  }, [slug, category]);

  // Apply filters
  const filteredProducts = useMemo(() => {
    let result = [...categoryProducts];

    // Pet type filter
    if (filters.petType.length > 0) {
      result = result.filter(p => filters.petType.includes(p.petType));
    }

    // Price range filter
    if (filters.priceRange.length > 0) {
      result = result.filter(p => {
        if (filters.priceRange.includes('under10') && p.price < 10) return true;
        if (filters.priceRange.includes('10to20') && p.price >= 10 && p.price < 20) return true;
        if (filters.priceRange.includes('20to30') && p.price >= 20 && p.price < 30) return true;
        if (filters.priceRange.includes('over30') && p.price >= 30) return true;
        return false;
      });
    }

    // Rating filter
    if (filters.rating) {
      result = result.filter(p => p.rating >= filters.rating);
    }

    // Sort
    switch (sortBy) {
      case 'price-low':
        result.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        result.sort((a, b) => b.price - a.price);
        break;
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      case 'newest':
        result.sort((a, b) => (b.isNewArrival ? 1 : 0) - (a.isNewArrival ? 1 : 0));
        break;
      default:
        result.sort((a, b) => (b.isBestSeller ? 1 : 0) - (a.isBestSeller ? 1 : 0));
    }

    return result;
  }, [categoryProducts, filters, sortBy]);

  const togglePetType = (type) => {
    setFilters(prev => ({
      ...prev,
      petType: prev.petType.includes(type)
        ? prev.petType.filter(t => t !== type)
        : [...prev.petType, type]
    }));
  };

  const togglePriceRange = (range) => {
    setFilters(prev => ({
      ...prev,
      priceRange: prev.priceRange.includes(range)
        ? prev.priceRange.filter(r => r !== range)
        : [...prev.priceRange, range]
    }));
  };

  const clearFilters = () => {
    setFilters({
      petType: [],
      priceRange: [],
      rating: null
    });
  };

  const activeFilterCount = filters.petType.length + filters.priceRange.length + (filters.rating ? 1 : 0);

  const FilterContent = () => (
    <div className="space-y-6">
      {/* Pet Type */}
      <div>
        <h4 className="font-heading font-bold text-gray-900 mb-3">Pet Type</h4>
        <div className="space-y-2">
          {['Dog', 'Cat'].map(type => (
            <div key={type} className="flex items-center">
              <Checkbox
                id={`pet-${type}`}
                checked={filters.petType.includes(type)}
                onCheckedChange={() => togglePetType(type)}
                data-testid={`filter-pet-${type.toLowerCase()}`}
              />
              <Label htmlFor={`pet-${type}`} className="ml-2 text-gray-700">{type}</Label>
            </div>
          ))}
        </div>
      </div>

      {/* Price Range */}
      <div>
        <h4 className="font-heading font-bold text-gray-900 mb-3">Price Range</h4>
        <div className="space-y-2">
          {[
            { id: 'under10', label: 'Under $10' },
            { id: '10to20', label: '$10 - $20' },
            { id: '20to30', label: '$20 - $30' },
            { id: 'over30', label: 'Over $30' },
          ].map(range => (
            <div key={range.id} className="flex items-center">
              <Checkbox
                id={`price-${range.id}`}
                checked={filters.priceRange.includes(range.id)}
                onCheckedChange={() => togglePriceRange(range.id)}
                data-testid={`filter-price-${range.id}`}
              />
              <Label htmlFor={`price-${range.id}`} className="ml-2 text-gray-700">{range.label}</Label>
            </div>
          ))}
        </div>
      </div>

      {/* Rating */}
      <div>
        <h4 className="font-heading font-bold text-gray-900 mb-3">Minimum Rating</h4>
        <div className="space-y-2">
          {[4, 4.5].map(rating => (
            <div key={rating} className="flex items-center">
              <Checkbox
                id={`rating-${rating}`}
                checked={filters.rating === rating}
                onCheckedChange={() => setFilters(prev => ({
                  ...prev,
                  rating: prev.rating === rating ? null : rating
                }))}
                data-testid={`filter-rating-${rating}`}
              />
              <Label htmlFor={`rating-${rating}`} className="ml-2 text-gray-700">
                {rating}+ Stars
              </Label>
            </div>
          ))}
        </div>
      </div>

      {activeFilterCount > 0 && (
        <Button
          variant="outline"
          onClick={clearFilters}
          className="w-full"
          data-testid="clear-filters-btn"
        >
          Clear All Filters
        </Button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FFF9F5]" data-testid="category-page">
      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="container-main py-4">
          <nav className="flex items-center text-sm text-gray-600">
            <Link to="/" className="hover:text-brand-orange" data-testid="breadcrumb-home">Home</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <span className="text-gray-900 font-medium">{category?.name || 'All Products'}</span>
          </nav>
        </div>
      </div>

      {/* Category Banner */}
      <div className="bg-gradient-to-r from-brand-orange to-brand-orange-hover text-white py-12" data-testid="category-banner">
        <div className="container-main">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-heading text-3xl md:text-4xl font-bold mb-2"
          >
            {category?.name || 'All Products'}
          </motion.h1>
          <p className="opacity-90">{category?.description || 'Browse our entire collection of pet toys'}</p>
        </div>
      </div>

      <div className="container-main section-padding">
        <div className="flex gap-8">
          {/* Desktop Sidebar Filters */}
          <aside className="hidden lg:block w-64 flex-shrink-0" data-testid="desktop-filters">
            <div className="sticky top-24 bg-white rounded-3xl p-6 shadow-sm">
              <h3 className="font-heading font-bold text-lg mb-4">Filters</h3>
              <FilterContent />
            </div>
          </aside>

          {/* Main Content */}
          <div className="flex-1">
            {/* Toolbar */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8 bg-white p-4 rounded-2xl shadow-sm" data-testid="category-toolbar">
              <div className="flex items-center gap-4">
                {/* Mobile Filter Button */}
                <Sheet open={isFilterOpen} onOpenChange={setIsFilterOpen}>
                  <SheetTrigger asChild>
                    <Button variant="outline" className="lg:hidden" data-testid="mobile-filter-btn">
                      <SlidersHorizontal className="w-4 h-4 mr-2" />
                      Filters
                      {activeFilterCount > 0 && (
                        <span className="ml-2 bg-brand-orange text-white w-5 h-5 rounded-full text-xs flex items-center justify-center">
                          {activeFilterCount}
                        </span>
                      )}
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="left" data-testid="mobile-filters">
                    <SheetHeader>
                      <SheetTitle className="font-heading">Filters</SheetTitle>
                    </SheetHeader>
                    <div className="mt-6">
                      <FilterContent />
                    </div>
                  </SheetContent>
                </Sheet>

                <span className="text-gray-600">{filteredProducts.length} products</span>
              </div>

              {/* Sort */}
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[180px]" data-testid="sort-select">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="featured">Featured</SelectItem>
                  <SelectItem value="price-low">Price: Low to High</SelectItem>
                  <SelectItem value="price-high">Price: High to Low</SelectItem>
                  <SelectItem value="rating">Best Rating</SelectItem>
                  <SelectItem value="newest">Newest</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Active Filters */}
            {activeFilterCount > 0 && (
              <div className="flex flex-wrap gap-2 mb-6" data-testid="active-filters">
                {filters.petType.map(type => (
                  <span
                    key={type}
                    className="inline-flex items-center gap-1 bg-brand-orange-light text-brand-orange px-3 py-1 rounded-full text-sm"
                  >
                    {type}
                    <button onClick={() => togglePetType(type)}>
                      <X className="w-4 h-4" />
                    </button>
                  </span>
                ))}
                {filters.priceRange.map(range => (
                  <span
                    key={range}
                    className="inline-flex items-center gap-1 bg-brand-orange-light text-brand-orange px-3 py-1 rounded-full text-sm"
                  >
                    {range}
                    <button onClick={() => togglePriceRange(range)}>
                      <X className="w-4 h-4" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Product Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6" data-testid="product-grid">
              {filteredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>

            {filteredProducts.length === 0 && (
              <div className="text-center py-16" data-testid="no-products">
                <p className="text-gray-600 mb-4">No products found matching your filters.</p>
                <Button onClick={clearFilters} className="btn-primary">
                  Clear Filters
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryPage;
